import { useEffect } from 'react'
import { Box, Tab, Tabs, Tooltip } from '@mui/material'
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { Suspense } from 'react'
import { activeTab, type NavSection } from './nav'
import { ErrorBoundary } from './ErrorBoundary'
import { PageFallback } from './PageFallback'
import { pruneTableKeys } from '@/lib/useTableState'
import { layout } from '@/theme/tokens'

/**
 * The same responsive value, pointing the other way.
 *
 * The values are CSS lengths with their unit written down — see the note on
 * `layout.page` — so negating means putting a sign in front of the string, not
 * arithmetic. A number here would go back through `theme.spacing` and the
 * margin would cancel a padding eight times its size.
 */
function negate(v: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(v).map(([bp, len]) => [bp, `-${len}`]))
}

/**
 * One section: its tab bar, and the page under it.
 *
 * ── WHY THIS IS A PATHLESS LAYOUT ROUTE ─────────────────────────────────────
 * Before this, `/money/margin` was a SIBLING of `/money` rather than nested, so
 * navigating between them unmounted everything and no shared chrome could
 * survive. A pathless layout route is a real nested route that contributes no
 * URL segment — which is exactly what "internal tabs without renaming a single
 * path" requires.
 *
 * ── AND WHY `<Suspense>` LIVES HERE, NOT IN THE SHELL ───────────────────────
 * With one boundary in `AppShell` wrapping the whole outlet, switching tabs
 * inside a section blanked the tab bar along with the page. Below the bar, the
 * bar never unmounts — which is the entire point of internal tabs.
 */
export function SectionLayout({ section }: { section: NavSection }) {
  const { pathname } = useLocation()
  const current = activeTab(pathname)
  const [params, setParams] = useSearchParams()

  /**
   * Drop table state belonging to tables that are not on screen.
   *
   * A table leaves the tree when you switch tabs, and its `<id>.f` key stays in
   * the URL — so a link to this section would carry filters from a pane the
   * recipient cannot see and cannot clear. The live ids are derived from the
   * tab that is open, not from a registry, because the calibration screen's
   * table ids are generated at runtime.
   */
  useEffect(() => {
    const next = pruneTableKeys(params, current)
    if (next.toString() !== params.toString()) setParams(next, { replace: true })
  }, [current, params, setParams])

  const panelId = `panel-${section.id}`

  return (
    <Box>
      {/* ONE tablist per page. Two nested tablists is wrong ARIA and visually
          heavy, so the top-level sections are a plain <nav> of links and only
          this bar is a real tablist. */}
      <Tabs
        value={current ?? false}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={`Vistas de ${section.label}`}
        // Manual activation, deliberately: every tab is a <Link>, so activating
        // on focus would navigate — and refetch — on every arrow keypress.
        // MUI's default gives roving focus with Enter to activate.
        sx={(t) => ({
          position: 'sticky',

          // ── EL INSET TIENE QUE IGUALAR AL MARGEN NEGATIVO ─────────────────
          // `top` NO se mide desde donde el margen negativo dejó la caja: el
          // navegador lo resuelve contra el borde de CONTENIDO del contenedor
          // que hace scroll, o sea `<main>` ya descontado su `py`. Con `top: 0`
          // la barra se repintaba 16px MÁS ABAJO de su posición en el flujo
          // mientras todo lo que va después se quedaba donde el margen lo puso
          // — 16px de la página tapados de forma permanente, y como la barra es
          // pegajosa, ningún scroll los revelaba nunca.
          //
          // Se veía como «la página está cortada debajo de la barra». En las
          // pantallas con `eyebrow` lo tapado era ese texto de 11px y pasaba por
          // un margen apretado; en `/access`, que no lo lleva, los 16px se
          // comían el `<h1>`.
          //
          // El mismo token que el margen, negado igual: si los dos no coinciden
          // reaparece la franja, en un breakpoint o en el otro.
          top: negate(layout.page.paddingY),
          zIndex: 3,
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: t.palette.background.default,
          '& .MuiTab-root': { minHeight: 40, py: 0 },

          // ── EL FONDO OPACO TIENE QUE CUBRIR TODA LA FRANJA FIJA ───────────
          // Un elemento pegajoso solo pinta su propia caja, y ésta medía 40px
          // con un `mb: 2` FUERA de ella. Ese hueco quedaba transparente y el
          // contenido de la página se veía pasar por él al desplazarse, justo
          // debajo de la barra: se lee como si la barra flotara sobre una
          // rendija. `pb` en vez de `mb` mete el hueco dentro de la caja opaca,
          // sin mover un pixel el resultado en reposo.
          pb: 2,

          // Y lo mismo por arriba y por los lados. `<main>` es el contenedor que
          // hace scroll y su `padding` pertenece al CONTENIDO, así que se
          // desplaza: sin esto queda una franja del ancho de ese padding por la
          // que el contenido asoma alrededor de la barra fija. El margen
          // negativo saca la caja hasta el borde del área de scroll y el padding
          // equivalente devuelve las pestañas a donde estaban.
          //
          // Derivado del MISMO token que usa `AppShell`, no tecleado: estos
          // números pasan por `theme.spacing`, así que `20` son 160px y no 20 —
          // un par escrito a mano se vería bien en un breakpoint y dejaría la
          // rendija en el otro.
          mt: negate(layout.page.paddingY),
          pt: layout.page.paddingY,
          mx: negate(layout.page.paddingX),
          px: layout.page.paddingX,
        })}
      >
        {section.tabs.map((tab) => (
          <Tab
            key={tab.to}
            value={tab.to}
            component={RouterLink}
            to={tab.to}
            aria-controls={panelId}
            label={
              tab.help ? (
                <Tooltip title={tab.help} enterDelay={600}>
                  <span>{tab.label}</span>
                </Tooltip>
              ) : (
                tab.label
              )
            }
          />
        ))}
      </Tabs>

      {/* El aire entre la barra y el contenido, UNA vez y para las cinco
          secciones. No lo tenía: el contenido de cada subpestaña arrancaba pegado
          al borde inferior de la barra.

          Y no es sólo estética. Esa barra es `position: sticky`, así que el
          contenido pasa POR DEBAJO de ella al hacer scroll — sin separación, la
          primera fila de una tabla queda a ras y, en cuanto se scrollea, medio
          renglón desaparece bajo la barra. El padding es lo que le da a esa fila
          un sitio donde existir.

          3 y no 2: entre la barra y la pantalla hay un cambio de cosa, no el
          siguiente elemento de una lista — el mismo `spacing={3}` que las
          pantallas usan para separar bloques distintos. */}
      <Box id={panelId} role="tabpanel" tabIndex={-1} sx={{ pt: 3 }}>
        {/* DENTRO del tabpanel y no envolviendo la barra: si una pantalla se
            rompe, la barra de pestañas tiene que sobrevivir para que el operador
            pueda irse a otra. Y `key={pathname}`, porque un boundary de clase se
            queda en error para siempre: sin la llave, la primera pantalla rota
            dejaría a toda la sección mostrando el error hasta un recargado. */}
        <ErrorBoundary key={pathname} scope="esta pantalla">
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  )
}
